import AppConfig from "../../config/config";
import { MediaRecord } from "../../interfaces/MediaObject";
import { RevampKeepDecision, RevampSaveMode } from "../../interfaces/ImageRevampObject";
import {
  ProductCardModal,
  ProductDetailsModal,
} from "../../interfaces/ProductObject";
import { rawMediaResourcesOf } from "../converters/backendToUi/productConvertor";
import { convertRawMediaList } from "../converters/backendToUi/mediaConvertor";

/**
 * Single source of truth for product media on the UI.
 *
 * `productMediaResources` ([{ mediaType, objectPath, section, thumbNail }],
 * nullable) is the ONLY source of product media. Public URLs are derived from
 * objectPath via the shared media converter. There are deliberately NO
 * fallbacks — no legacy details.images / media.imageUrlList, no guessing
 * conventional storage paths. A product with no (or null) mediaResources has
 * no media: components show the default fallback image (Assets.PLACEHOLDERS).
 *
 * Rendering components must use these URLs directly as <img>/<video> sources —
 * never probe storage with fetch()/blobs.
 */

/**
 * Full normalized media list for a product — images, videos AND PDFs. Each
 * record carries mediaType / objectPath / publicUrl, ready for the shared
 * MediaRecord renderers (MediaTile, MediaViewer). Empty when
 * productMediaResources is null/absent/empty.
 */
export function mediaRecordsFromProduct(
  product: ProductDetailsModal | ProductCardModal
): MediaRecord[] {
  const records = convertRawMediaList(rawMediaResourcesOf(product));
  // Defensive dedupe by objectPath: the backend's append-only update has been
  // observed to double-write records. Same path = same bytes, so dropping
  // repeats is always safe and keeps first-occurrence order.
  const seen = new Set<string>();
  return records.filter((record) => {
    const key = record.objectPath || record.publicUrl;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Maps the curator's Keep/Remove choices to what the save must do. Returns
 * null when nothing should be saved: either the revamp is discarded (keep
 * original only) or the choice is invalid (neither kept — the UI blocks it,
 * and a product must never lose its only photo).
 */
export function revampSaveMode(decision: RevampKeepDecision): RevampSaveMode | null {
  if (!decision.keepRevamped) return null;
  return decision.keepOriginal ? "add" : "swap";
}

/**
 * The product's full media list after saving a revamp. The backend save is a
 * declarative full replace, so this must return EVERY record to keep.
 * - swap: the revamp takes the original's slot.
 * - add: the revamp goes first (becomes the card's hero), all others follow.
 */
export function applyRevampToMedia(
  current: MediaRecord[],
  original: MediaRecord,
  revamped: MediaRecord,
  mode: RevampSaveMode
): MediaRecord[] {
  return mode === "swap"
    ? current.map((r) => (r.objectPath === original.objectPath ? revamped : r))
    : [revamped, ...current];
}

/** One entry of the product details gallery — images and videos, inline. */
export interface ProductGalleryItem {
  kind: "image" | "video";
  url: string;
}

/**
 * Gallery items for the product details page: images AND videos in exact
 * payload order (the backend's order is the display order). PDFs are not
 * swipeable visual media — they render in the separate Documents section.
 */
export function productGalleryItems(
  product: ProductDetailsModal
): ProductGalleryItem[] {
  return mediaRecordsFromProduct(product)
    .map((record) => {
      if (record.mediaType.startsWith("image/")) {
        return { kind: "image" as const, url: record.publicUrl };
      }
      if (record.mediaType.startsWith("video/")) {
        return { kind: "video" as const, url: record.publicUrl };
      }
      return null;
    })
    .filter((item): item is ProductGalleryItem => item !== null);
}

/**
 * Image URLs (image/* records only — products may also carry videos/PDFs),
 * in payload order. Empty when the product has no images: callers render
 * their default fallback image, nothing is guessed or probed.
 */
export function productImageUrls(
  product: ProductDetailsModal | ProductCardModal
): string[] {
  return mediaRecordsFromProduct(product)
    .filter((record) => record.mediaType.startsWith("image/"))
    .map((record) => record.publicUrl);
}

/**
 * @deprecated Only for the legacy probe helpers in ProductApiService
 * (getImage/getAllImages, themselves deprecated). Display code must not
 * construct storage URLs — use productImageUrls.
 */
export function conventionalProductImageUrl(
  productId: string,
  index = 1
): string {
  return `${AppConfig.supabaseStorageDomain}/storage/v1/object/public/product-images/products/${productId}/product-image${index}.jpeg`;
}
